/**
 * What a phone or the console may approve, by organisation and by person (packages/shared/src/schemas/remoteApprovals.ts).
 * The policy says per risk level whether a tool call may be approved from anywhere or only at the machine; an
 * administrator may leave a level to each person instead, and each person then chooses for their own workbenches.
 * A node is shared, so it always follows the policy.
 */

import { REMOTE_APPROVAL_RISKS, type RemoteApprovalRisk, type RemoteApprovalValue } from '@aioe/shared';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { ErrorNote, ListBlock, ListGroup, ListRow, Toggle } from './ui.tsx';

export const RISK_TEXT: Record<RemoteApprovalRisk, string> = { low: 'Low-risk requests', medium: 'Medium-risk requests', high: 'High-risk requests' };
export const RISK_HINT: Record<RemoteApprovalRisk, string> = {
  low: 'Reading files, running tests.',
  medium: 'Writing outside the repository, installing packages.',
  high: 'Deleting, pushing to a protected branch, spending money.',
};
export const VALUE_TEXT: Record<RemoteApprovalValue, string> = { allow: 'From anywhere', deny: 'At the machine only' };

/**
 * The organisation's rule, for the policy editor: whether each person decides a level for their own machines. It is
 * saved at once and applies to every policy; the policy's own answer stays what people get until they choose.
 */
export function usePersonDecides() {
  const rules = useLoad(() => routes.remoteApprovalRules().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const decides = rules.data?.personDecides ?? [];
  const set = async (risk: RemoteApprovalRisk, on: boolean) => {
    setError(undefined);
    setSaving(true);
    try {
      await routes.setRemoteApprovalRules({ personDecides: on ? [...decides, risk] : decides.filter((r) => r !== risk) });
      rules.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };
  return { loaded: Boolean(rules.data), decides, set, saving, error };
}

export function PersonDecidesToggle({ risk, state }: { risk: RemoteApprovalRisk; state: ReturnType<typeof usePersonDecides> }) {
  if (!state.loaded) return null;
  return (
    <label className="flex items-center justify-end gap-2 text-xs text-muted">
      Each person decides
      <Toggle label={`Each person decides ${risk}-risk approvals`} checked={state.decides.includes(risk)} isDisabled={state.saving} onChange={(v) => void state.set(risk, v)} />
    </label>
  );
}

/** Settings, You: the person's own choice where the organisation leaves it to them, and what applies to them. */
export function MyRemoteApprovalsPanel() {
  const view = useLoad(() => routes.myRemoteApprovals().catch(() => undefined), []);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const v = view.data;
  if (!v) return null;
  const choose = async (risk: RemoteApprovalRisk, value: RemoteApprovalValue) => {
    setError(undefined);
    setSaving(true);
    try {
      await routes.setMyRemoteApprovals({ ...v.choices, [risk]: value });
      view.reload();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ListGroup
      id="approvals"
      description={`When an agent on one of your workbenches asks to use a tool, where you may say yes. ${v.personDecides.length ? 'Your organisation leaves some of this to you.' : 'Your organisation decides this for everyone.'}`}
    >
      {REMOTE_APPROVAL_RISKS.map((risk) => (
        <ListRow
          key={risk}
          title={RISK_TEXT[risk]}
          description={RISK_HINT[risk]}
          control={
            v.personDecides.includes(risk) ? (
              <Segment aria-label={`${RISK_TEXT[risk]}: where you may approve them`} size="sm" selectedKey={v.effective[risk]} isDisabled={saving} onSelectionChange={(k) => void choose(risk, String(k) as RemoteApprovalValue)} className="w-fit">
                {(Object.keys(VALUE_TEXT) as RemoteApprovalValue[]).map((k) => (
                  <Segment.Item key={k} id={k}>
                    {VALUE_TEXT[k]}
                  </Segment.Item>
                ))}
              </Segment>
            ) : undefined
          }
          value={v.personDecides.includes(risk) ? undefined : `${VALUE_TEXT[v.effective[risk]]}, set by your organisation`}
        />
      ))}
      {error ? (
        <ListBlock>
          <ErrorNote error={error} />
        </ListBlock>
      ) : null}
    </ListGroup>
  );
}
